import React from 'react';
import { Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { Box, VStack, HStack, Text, Button, Divider } from '@chakra-ui/react';
import AdminLayout from '../components/Layout/AdminLayout';
import UserLayout from '../components/Layout/UserLayout';
import EventScheduling from '../pages/EventScheduling';
import Dashboard from '../pages/Dashboard';
import Analytics from '../pages/Analytics';
import Issues from '../pages/Issues';
import Client from '../pages/Client';
import Report from '../pages/Report';
import MapReport from '../pages/MapReport';
import ReportResult from '../pages/ReportResult';
import Notifications from '../pages/Notifications';
import UserManagement from '../pages/UserManagement';
import UserDashboard from '../pages/UserDashboard';
import UserNotifications from '../pages/UserNotifications';
import LoginPage from '../components/Auth/LoginPage';
import { AuthService } from '../services/authService';
import ApplicationsManagement from '../pages/ApplicationsManagement';
import ClientMappingsManagement from '../pages/ClientMappingManagement';
import ClientProfilePage from '../pages/ClientProfile';
import UrlManager from '../pages/UrlManager';
import ChecklistManager from '../pages/CheckListManager';
import ChecklistMapping from '../pages/CheckListMapping';
import ChecklistResults from '../pages/CheckListResult';
import DynamicReports from '../pages/DynamicReports';
import DynamicReportMappings from '../pages/DynamicReportMappings';
import DynamicReportResults from '../pages/DynamicReportResults';
import MarketDataResults from '../pages/MarketDataResults';

/**
 * Application routing configuration with role-based access control
 */
function Routes({ userProfile, appConfig, onError, isAuthenticated: parentIsAuthenticated, authServiceReady, userStatus }) {
    const isAuthenticated = useIsAuthenticated();
    const location = useLocation();

    // Use parent auth state if provided, otherwise use hook
    const authState = parentIsAuthenticated !== undefined ? parentIsAuthenticated : isAuthenticated;

    /**
     * Authentication check with user status validation
     */
    const isFullyAuthenticated = () => {
        return authState && userProfile && userProfile.email && userStatus === 'approved';
    };

    /**
     * Check if user has admin role
     * Used for admin-only route protection
     */
    const isAdmin = () => {
        return isFullyAuthenticated() && userProfile?.role === 'admin';
    };

    /**
     * Check if user has user role (Support desk user)
     * Used for user-only route protection
     */
    const isUser = () => {
        return isFullyAuthenticated() && userProfile?.role === 'user';
    };

    /**
     * Get user dashboard path based on role
     * Returns appropriate dashboard route for the user's role
     */
    const getUserDashboardPath = () => {
        if (!isFullyAuthenticated()) {
            return '/login';
        }
        
        switch (userProfile?.role) {
            case 'admin':
                return '/analytics';
            case 'user':
                return '/user-analytics';
            default:
                return '/login'; // Fallback for unknown roles
        }
    };

    /**
     * Protected route wrapper component
     */
    const ProtectedRoute = ({ children, requireAdmin = false, requireUser = false }) => {
        // Check if user is authenticated but has pending/rejected status
        if (authState && userProfile && userProfile.email) {
            if (userStatus === 'pending') {
                // User is authenticated but pending approval - don't redirect to prevent loops
                return null; // This will trigger the error state in App.js
            }
            
            if (userStatus === 'rejected' || userStatus === 'suspended') {
                // User is authenticated but rejected/suspended - don't redirect to prevent loops
                return null; // This will trigger the error state in App.js
            }
            
            if (userStatus === 'error') {
                // User creation or validation failed - don't redirect to prevent loops
                return null; // This will trigger the error state in App.js
            }
        }

        // Check if user is fully authenticated (MSAL + profile + approved status)
        if (!isFullyAuthenticated()) {
            // Only store attempted route if we're not already on login and user isn't in error state
            if (location.pathname !== '/login' && !userStatus) {
                sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
            }
            
            return <Navigate to="/login" replace />;
        }

        // Check role-based access
        if (requireAdmin && !isAdmin()) {
            // Admin required but user doesn't have admin role
            return <Navigate to={getUserDashboardPath()} replace />;
        }

        if (requireUser && !isUser()) {
            // User role required but user doesn't have user role
            return <Navigate to={getUserDashboardPath()} replace />;
        }

        return children;
    };

    /**
     * Public route wrapper component
     */
    const PublicRoute = ({ children }) => {
        // If user is authenticated but has status issues, show login page
        if (authState && userProfile && userProfile.email) {
            if (userStatus === 'pending' || userStatus === 'rejected' || userStatus === 'suspended' || userStatus === 'error') {
                // Don't redirect, stay on login page to show error state
                return children;
            }
        }

        if (isFullyAuthenticated()) {
            // Check for stored redirect path
            const redirectPath = sessionStorage.getItem('redirectAfterLogin');
            
            if (redirectPath && redirectPath !== '/login') {
                sessionStorage.removeItem('redirectAfterLogin');
                return <Navigate to={redirectPath} replace />;
            }
            
            // Default redirect based on user role
            return <Navigate to={getUserDashboardPath()} replace />;
        }

        return children;
    };

    /**
     * Admin layout wrapper for admin-only routes
     * Provides consistent layout structure for all admin pages
     * Passes down application context and error handlers
     */
    const AdminLayoutWrapper = ({ children }) => (
        <ProtectedRoute requireAdmin={true}>
            <AdminLayout 
                userProfile={userProfile}
                appConfig={appConfig}
                onError={onError}
                authServiceReady={authServiceReady}
                userStatus={userStatus}
            >
                {children}
            </AdminLayout>
        </ProtectedRoute>
    );

    /**
     * User layout wrapper for user-only routes
     * Provides consistent layout structure for Support desk user pages
     * Similar to admin layout but with limited navigation options
     */
    const UserLayoutWrapper = ({ children }) => (
        <ProtectedRoute requireUser={true}>
            <UserLayout 
                userProfile={userProfile}
                appConfig={appConfig}
                onError={onError}
                authServiceReady={authServiceReady}
                userStatus={userStatus}
            >
                {children}
            </UserLayout>
        </ProtectedRoute>
    );

    return (
        <RouterRoutes>
            {/* Authentication Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage 
                            appConfig={appConfig}
                            onError={onError}
                            authServiceReady={authServiceReady}
                            userStatus={userStatus}
                        />
                    </PublicRoute>
                } 
            />

            {/* ================================ ADMIN ROUTES ================================ */}
            
            {/* Admin Dashboard Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <AdminLayoutWrapper>
                        <Dashboard 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Analytics Routes */}
            <Route 
                path="/analytics" 
                element={
                    <AdminLayoutWrapper>
                        <Analytics 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Client Management Routes */}
            <Route 
                path="/clients" 
                element={
                    <AdminLayoutWrapper>
                        <Client 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Report Management Routes */}
            <Route 
                path="/reports" 
                element={
                    <AdminLayoutWrapper>
                        <Report 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Report Mapping Routes */}
            <Route 
                path="/map-reports" 
                element={
                    <AdminLayoutWrapper>
                        <MapReport 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Report Results Routes */}
            <Route 
                path="/report-results" 
                element={
                    <AdminLayoutWrapper>
                        <ReportResult 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />
            <Route 
                path="/client-applications" 
                element={
                    <AdminLayoutWrapper>
                       <ApplicationsManagement
                        userProfile={userProfile}
                        appConfig={appConfig}
                        authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />
            <Route 
                path="/url-manager" 
                element={
                    <AdminLayoutWrapper>
                       <UrlManager
                        userProfile={userProfile}
                        appConfig={appConfig}
                        authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* Admin Notifications Routes */}
            <Route 
                path="/notifications" 
                element={
                    <AdminLayoutWrapper>
                        <Notifications 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />

            {/* User Management Routes (Admin Only) */}
            <Route 
                path="/user-management" 
                element={
                    <AdminLayoutWrapper>
                        <UserManagement 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                } 
            />
            <Route
                path="/checklist-manager"
                element={
                    <AdminLayoutWrapper>
                        <ChecklistManager
                         userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                }
            />
            <Route
                path="/dynamic-reports"
                element={
                    <AdminLayoutWrapper>
                        <DynamicReports
                         userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                }
            />

            <Route
                path="/dynamic-report-mappings"
                element={
                    <AdminLayoutWrapper>
                        <DynamicReportMappings
                         userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </AdminLayoutWrapper>
                }
            />

            {/* ================================ USER ROUTES ================================ */}
            
            {/* User Dashboard Routes (Support Desk User) */}
            <Route 
                path="/user-analytics" 
                element={
                    <UserLayoutWrapper>
                        <UserDashboard 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </UserLayoutWrapper>
                } 
            />

            {/* User Notifications Routes (Support Desk User) */}
            <Route 
                path="/user-notifications" 
                element={
                    <UserLayoutWrapper>
                        <UserNotifications 
                            userProfile={userProfile}
                            appConfig={appConfig}
                            authServiceReady={authServiceReady}
                        />
                    </UserLayoutWrapper>
                } 
            />
            {/* ================================ SHARED ROUTES ================================ */}

            {/* Issues Management Routes - Single route that works for both admin and user */}
            <Route 
                path="/issues" 
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <Issues 
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <Issues 
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/clientprofile" 
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <ClientProfilePage 
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <ClientProfilePage 
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                } 
            />
            
            <Route
                path="/event-scheduling"
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper
                            >
                                <EventScheduling
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper
                            >
                                <EventScheduling
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/client-appmappings" 
                element={

                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <ClientMappingsManagement
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ):(
                        <UserLayoutWrapper>
                            <ClientMappingsManagement
                                userProfile={userProfile}
                                appConfig={appConfig}
                                authServiceReady={authServiceReady}
                            />
                        </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/checklist-results" 
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <ChecklistResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <ChecklistResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/market-data-results" 
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <MarketDataResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <MarketDataResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                } 
            />
             <Route
                path="/checklist-mappings"
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <ChecklistMapping
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <ChecklistMapping
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dynamic-report-results"
                element={
                    <ProtectedRoute>
                        {isAdmin() ? (
                            <AdminLayoutWrapper>
                                <DynamicReportResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <DynamicReportResults
                                    userProfile={userProfile}
                                    appConfig={appConfig}
                                    authServiceReady={authServiceReady}
                                />
                            </UserLayoutWrapper>
                        )}
                    </ProtectedRoute>
                }
            />
            
            {/* ================================ DEFAULT ROUTES ================================ */}

            {/* Default Route Handling - Enhanced Logic with Role-based Redirection */}
            <Route 
                path="/" 
                element={
                    <Navigate to={
                        isFullyAuthenticated() ? getUserDashboardPath() : '/login'
                    } replace />
                } 
            />

            {/* Catch-all Route for 404 handling - Role-based layout with status checking */}
            <Route 
                path="*" 
                element={
                    isFullyAuthenticated() ? (
                        isAdmin() ? (
                            <AdminLayoutWrapper>
                                <NotFoundPage />
                            </AdminLayoutWrapper>
                        ) : (
                            <UserLayoutWrapper>
                                <NotFoundPage />
                            </UserLayoutWrapper>
                        )
                    ) : (
                        <Navigate to="/login" replace />
                    )
                } 
            />
        </RouterRoutes>
    );
}

/**
 * 404 Not Found page component
 */
const NotFoundPage = () => {
    const location = useLocation();

    const handleGoBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            // Role-based fallback
            const user = AuthService.getCurrentUser();
            const fallbackPath = user?.role === 'admin' ? '/dashboard' : '/user-analytics';
            window.location.href = fallbackPath;
        }
    };

    const handleGoHome = () => {
        // Role-based home redirect
        const user = AuthService.getCurrentUser();
        const homePath = user?.role === 'admin' ? '/dashboard' : '/user-analytics';
        window.location.href = homePath;
    };

    // Get user role for context-appropriate messaging
    const user = AuthService.getCurrentUser();
    const isAdmin = user?.role === 'admin';

    return (
        <Box 
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            py={12}
            px={6}
            minH="70vh"
            w="full"
        >
            <VStack spacing={6} maxW="600px" w="full">
                {/* 404 Error Display */}
                <Text
                    fontSize={{ base: '4xl', md: '6xl' }}
                    fontWeight="bold"
                    color="brand.500"
                    lineHeight="1"
                    mb={2}
                >
                    404
                </Text>
                
                {/* Error Message */}
                <Text
                    fontSize={{ base: 'xl', md: '2xl' }}
                    fontWeight="600"
                    color="gray.700"
                    _dark={{ color: 'gray.200' }}
                >
                    Page Not Found
                </Text>
                
                {/* Description */}
                <Text
                    fontSize="md"
                    color="gray.600"
                    _dark={{ color: 'gray.400' }}
                    lineHeight="1.6"
                    maxW="500px"
                >
                    The page <Text as="span" fontWeight="semibold" color="brand.500">"{location.pathname}"</Text> you're looking for doesn't exist or has been moved.
                </Text>
                
                {/* Action Buttons */}
                <HStack spacing={4} flexWrap="wrap" justify="center" pt={4}>
                    <Button
                        colorScheme="brand"
                        size="lg"
                        onClick={handleGoBack}
                        leftIcon={<span>←</span>}
                        _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg'
                        }}
                        transition="all 0.2s"
                    >
                        Go Back
                    </Button>
                    
                    <Button
                        variant="outline"
                        colorScheme="brand"
                        size="lg"
                        onClick={handleGoHome}
                        leftIcon={<span>🏠</span>}
                        _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg'
                        }}
                        transition="all 0.2s"
                    >
                        Go to {isAdmin ? 'Dashboard' : 'Home'}
                    </Button>
                </HStack>
                
                {/* Divider */}
                <Divider my={6} />
                
                {/* Role-based Helpful Links */}
                <VStack spacing={3}>
                    <Text
                        fontSize="sm"
                        fontWeight="600"
                        color="gray.600"
                        _dark={{ color: 'gray.400' }}
                    >
                        Quick Navigation:
                    </Text>
                    <HStack 
                        spacing={6} 
                        flexWrap="wrap" 
                        justify="center"
                        fontSize="sm"
                    >
                        <Text
                            as="a"
                            href={isAdmin ? "/dashboard" : "/user-analytics"}
                            color="brand.500"
                            _hover={{ 
                                color: "brand.600",
                                textDecoration: "underline"
                            }}
                            transition="color 0.2s"
                        >
                            {isAdmin ? "Dashboard" : "Home"}
                        </Text>
                        
                        {isAdmin && (
                            <Text
                                as="a"
                                href="/analytics"
                                color="brand.500"
                                _hover={{ 
                                    color: "brand.600",
                                    textDecoration: "underline"
                                }}
                                transition="color 0.2s"
                            >
                                Analytics
                            </Text>
                        )}
                        
                        {/* Issues link for both admin and user */}
                        <Text
                            as="a"
                            href="/issues"
                            color="brand.500"
                            _hover={{ 
                                color: "brand.600",
                                textDecoration: "underline"
                            }}
                            transition="color 0.2s"
                        >
                            Issues
                        </Text>
                        
                        {isAdmin && (
                            <>
                                <Text
                                    as="a"
                                    href="/clients"
                                    color="brand.500"
                                    _hover={{ 
                                        color: "brand.600",
                                        textDecoration: "underline"
                                    }}
                                    transition="color 0.2s"
                                >
                                    Clients
                                </Text>
                                <Text
                                    as="a"
                                    href="/reports"
                                    color="brand.500"
                                    _hover={{ 
                                        color: "brand.600",
                                        textDecoration: "underline"
                                    }}
                                    transition="color 0.2s"
                                >
                                    Reports
                                </Text>
                                <Text
                                    as="a"
                                    href="/user-management"
                                    color="brand.500"
                                    _hover={{ 
                                        color: "brand.600",
                                        textDecoration: "underline"
                                    }}
                                    transition="color 0.2s"
                                >
                                    User Management
                                </Text>
                            </>
                        )}
                        
                        <Text
                            as="a"
                            href={isAdmin ? "/notifications" : "/user-notifications"}
                            color="brand.500"
                            _hover={{ 
                                color: "brand.600",
                                textDecoration: "underline"
                            }}
                            transition="color 0.2s"
                        >
                            Notifications
                        </Text>
                    </HStack>
                </VStack>
                
                {/* Debug Information (Development Only) */}
                {process.env.NODE_ENV === 'development' && (
                    <Box
                        mt={8}
                        p={4}
                        bg="gray.50"
                        _dark={{ bg: 'gray.300' }}
                        borderRadius="md"
                        fontSize="xs"
                        color="gray.600"
                        fontFamily="mono"
                        maxW="400px"
                        w="full"
                    >
                        <Text fontWeight="bold" mb={2}>Debug Information:</Text>
                        <Text>Requested Path: {location.pathname}</Text>
                        <Text>Search Params: {location.search || 'none'}</Text>
                        <Text>User Role: {user?.role || 'unknown'}</Text>
                        <Text>User Status: {user?.status || 'unknown'}</Text>
                        <Text>Timestamp: {new Date().toISOString()}</Text>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

export default Routes;